import endOfDay from 'date-fns/endOfDay';

import { ONE_MINUTE_MILLISECONDS } from '../../dateTime';
import { CloudFrontProgram } from '../composed';
import { cloudFrontConfig } from '../network/cloudFrontConfig';
import { requestCloudFront } from '../network/requestCloudFront';

type FetchEpgArgs = {
  apiVersion?: string;
  channels?: Array<number>;
  pageIndex?: number;
  pageSize?: 100 | 200 | 300 | 400 | 500;
  responseProfileId?: 1;
  slots: '1' | '2' | '3' | '4' | '5' | '6' | 'all'; // TODO: type slot ranges, for instance 1-2, 3-6
  timestamp: number; // UTC time, multiplication of 86400 (Time 00:00)
};

type EpgResponse = {
  epgChunk: Record<string, CloudFrontProgram[]>;
};

/**
 *
 * @see https://kaltura.atlassian.net/wiki/spaces/SOLENG/pages/3270935329/EPG+retrieval+LLD#Use-case-2:-Getting-EPG-using-New-API
 */
export function fetchEpg({
  apiVersion,
  channels,
  pageIndex,
  pageSize,
  responseProfileId,
  slots,
  timestamp,
}: FetchEpgArgs) {
  const { partnerId } = cloudFrontConfig;

  const startDate = new Date(timestamp);
  const endDate = endOfDay(startDate);

  // CF works against utc timezone(+0) since ingested timestamps are in UTC+0
  // UTC+0 midnight(12pm) is 3am UTC+3
  // so UTC+3 midnight is 9pm UTC+0 therefore we need to send timezone offset
  // to get the correct result. Offset is a difference in minutes, For UTC+3 it should be 180
  const startDateOffset = Math.abs(startDate.getTimezoneOffset());
  const endDateOfsset = Math.abs(endDate.getTimezoneOffset());

  // It spring(EU), timezone moves from +1(-60) to +2(-120)
  // therefore, we need to send 60 - relative difference between timezones, to get programs for 23 hours
  // It autumn(EU), timezone moves from +2(-120) to +1(-60)
  // therefore, we need to send -60 - relative difference between timezones, to get programs for 25 hours

  const dstOffset = endDateOfsset - startDateOffset;
  const shouldApplyDstOffset = dstOffset !== 0;

  const utcEpoch = (timestamp + startDateOffset * ONE_MINUTE_MILLISECONDS) / 1000;

  // The channel list must be sent in ascending order of channels ids!
  const channelsQuery = channels ? channels.sort((a, b) => a - b).join(',') : undefined;

  return requestCloudFront<EpgResponse>({
    path: `/epg/action/get/partnerid/${partnerId}/date/${utcEpoch}/slots/${slots}`,
    params: {
      offsetInMin: startDateOffset,
      ...(apiVersion && { apiVersion }),
      ...(channels && { channels: channelsQuery }),
      ...(shouldApplyDstOffset && { dstoffsetupdateminutes: dstOffset }),
      ...(pageSize && { pageSize }),
      ...(pageIndex && { pageIndex }),
      ...(responseProfileId && { responseProfileId }),
    },
  });
}
